/**
 * Policy Library — Biblioteca de Políticas com geração IA + versionamento.
 * Route: /policies
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileText, Plus, Sparkles, ShieldCheck, CheckCircle2, Clock, AlertTriangle, Loader2, Eye, Wand2, GitCompare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VerifyReviewsButton } from '../components/VerifyReviewsButton';

const STATUS_META = {
  draft: { label: 'Rascunho', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  under_review: { label: 'Em revisão', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Aprovada', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  archived: { label: 'Arquivada', tone: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const CATEGORY_LABEL = {
  security: 'Segurança', incident: 'Incidentes', continuity: 'Continuidade',
  suppliers: 'Fornecedores', access: 'Acessos', ai: 'IA', hr: 'RH', other: 'Outros',
};

export default function Policies() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'ciso';

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ template_id: '', title: '', category: 'security', description: '', review_cycle_months: 12 });
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [approving, setApproving] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const openDiff = async (snapshot_id, against = 'current') => {
    if (!viewing) return;
    setDiffLoading(true);
    try {
      const { data } = await api.get(
        `/tasks/reviews/policy-snapshots/${viewing.policy_id}/diff`,
        { params: { snapshot_id, against } }
      );
      setDiffData(data);
    } catch {
      toast.error('Falha ao carregar diff.');
    } finally {
      setDiffLoading(false);
    }
  };

  useEffect(() => {
    if (!viewing?.policy_id) { setSnapshots([]); return; }
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/tasks/reviews/policy-snapshots/${viewing.policy_id}`);
        if (!ignore) setSnapshots(data.snapshots || []);
      } catch { if (!ignore) setSnapshots([]); }
    })();
    return () => { ignore = true; };
  }, [viewing?.policy_id]);

  useEffect(() => { document.title = 'Políticas · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const [s, list, tpl] = await Promise.all([
          api.get('/policies/stats'),
          api.get('/policies'),
          api.get('/policies/templates'),
        ]);
        if (ignore) return;
        setStats(s.data);
        setItems(list.data.items || []);
        setTemplates(tpl.data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar políticas.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [refresh]);

  const openCreate = () => {
    setCreateForm({ template_id: '', title: '', category: 'security', description: '', review_cycle_months: 12 });
    setShowCreate(true);
  };

  const applyTemplate = (tid) => {
    const tpl = templates.find(t => t.template_id === tid);
    if (!tpl) return;
    setCreateForm({
      template_id: tid,
      title: tpl.title,
      category: tpl.category,
      description: tpl.description,
      review_cycle_months: tpl.review_cycle_months,
    });
  };

  const submitCreate = async () => {
    if (!createForm.title || createForm.title.length < 5) { toast.error('Título mínimo 5 caracteres.'); return; }
    setSaving(true);
    try {
      const payload = { ...createForm };
      if (!payload.template_id) delete payload.template_id;
      const { data } = await api.post('/policies', payload);
      toast.success(`Política criada: ${data.title}`);
      setShowCreate(false);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao criar.');
    } finally { setSaving(false); }
  };

  const openView = async (pol) => {
    try {
      const { data } = await api.get(`/policies/${pol.policy_id}`);
      setViewing(data);
      const current = (data.versions || []).find(v => v.version === data.current_version) || (data.versions || []).slice(-1)[0] || null;
      setSelectedVersion(current);
    } catch (err) { toast.error('Falha ao abrir.'); }
  };

  const draftAi = async () => {
    if (!viewing) return;
    setDrafting(true);
    const startVersions = (viewing.versions || []).length;
    try {
      const { data } = await api.post(`/policies/${viewing.policy_id}/draft-ai`);
      if (data.status === 'in_progress') {
        toast.info('Geração já em curso. A aguardar…');
      } else {
        toast.info(`Geração iniciada · Claude Sonnet 4.6 · ~${data.estimated_seconds || 90}s`);
      }
      // Poll every 5s up to 3 minutes
      const startAt = Date.now();
      const poll = async () => {
        try {
          const { data: fresh } = await api.get(`/policies/${viewing.policy_id}`);
          if ((fresh.versions || []).length > startVersions) {
            const newVer = fresh.versions[fresh.versions.length - 1];
            setViewing(fresh);
            setSelectedVersion(newVer);
            setRefresh(r => r + 1);
            toast.success(`Versão ${newVer.version} gerada (${(newVer.content_md || '').split(/\s+/).length} palavras).`);
            setDrafting(false);
            return;
          }
          if (fresh.ai_draft_last_error) {
            toast.error(`Falha IA: ${fresh.ai_draft_last_error.slice(0, 120)}`);
            setDrafting(false);
            return;
          }
          if (Date.now() - startAt > 180000) {
            toast.warning('Timeout de polling (3 min). Recarregue a página para ver o estado.');
            setDrafting(false);
            return;
          }
          setTimeout(poll, 5000);
        } catch {
          setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao iniciar geração IA.');
      setDrafting(false);
    }
  };

  const approveVersion = async (ver) => {
    if (!viewing || !ver) return;
    setApproving(ver.version);
    try {
      const { data } = await api.post(`/policies/${viewing.policy_id}/versions/${ver.version}/approve`);
      toast.success(`Versão ${data.version} aprovada · hash ${data.signature_hash.slice(0, 10)}…`);
      const { data: fresh } = await api.get(`/policies/${viewing.policy_id}`);
      setViewing(fresh);
      setSelectedVersion((fresh.versions || []).find(v => v.version === ver.version));
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha.');
    } finally { setApproving(null); }
  };

  return (
    <Layout title="Biblioteca de Políticas">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="policies-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-indigo-600" /> Biblioteca de Políticas
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Templates NIS2/AI Act, geração IA da primeira versão, versionamento, ciclo de revisão e aprovação com assinatura SHA-256.
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <VerifyReviewsButton />
              <Button onClick={openCreate} data-testid="policies-new-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Nova política
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { key: 'total', label: 'Total', value: stats?.total ?? 0, icon: FileText, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'approved', label: 'Aprovadas', value: stats?.approved ?? 0, icon: CheckCircle2, tone: 'from-emerald-500 to-emerald-600' },
            { key: 'under_review', label: 'Em revisão', value: stats?.under_review ?? 0, icon: Clock, tone: 'from-amber-500 to-amber-600' },
            { key: 'draft', label: 'Rascunhos', value: stats?.draft ?? 0, icon: FileText, tone: 'from-slate-500 to-slate-600' },
            { key: 'review_due', label: 'Revisão em atraso', value: stats?.review_due ?? 0, icon: AlertTriangle, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`policies-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Todas as políticas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              items.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Nenhuma política ainda. Use &quot;Nova política&quot; para começar (com template + geração IA).</div> :
              <div className="divide-y divide-slate-100">
                {items.map(p => {
                  const st = STATUS_META[p.status] || STATUS_META.draft;
                  const vers = (p.versions || []).length;
                  return (
                    <div key={p.policy_id} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => openView(p)} data-testid={`policy-${p.policy_id}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{CATEGORY_LABEL[p.category] || p.category}</Badge>
                            <Badge variant="secondary" className={st.tone}>{st.label}</Badge>
                            {p.current_version && <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">v{p.current_version}</Badge>}
                            <span className="text-xs text-slate-500">{vers} versão{vers !== 1 ? 'ões' : ''}</span>
                            {p.next_review_date && <span className="text-xs text-slate-500">· Próx. rev: {p.next_review_date}</span>}
                          </div>
                          <h3 className="font-semibold text-slate-900">{p.title}</h3>
                          {p.description && <p className="text-sm text-slate-600 line-clamp-2 mt-1">{p.description}</p>}
                          {(p.legal_refs || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {p.legal_refs.map((r, i) => <span key={i} className="text-[10px] text-slate-500">⚖️ {r}</span>)}
                            </div>
                          )}
                        </div>
                        <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl" data-testid="policy-create-dialog">
          <DialogHeader>
            <DialogTitle>Nova política</DialogTitle>
            <DialogDescription>Escolha um template pré-construído (recomendado) ou crie do zero.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template</Label>
              <Select value={createForm.template_id} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="create-template"><SelectValue placeholder="— Sem template —" /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título *</Label>
              <Input value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} data-testid="create-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={createForm.category} onValueChange={v => setCreateForm({ ...createForm, category: v })}>
                  <SelectTrigger data-testid="create-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ciclo de revisão (meses)</Label>
                <Input type="number" min={1} max={60} value={createForm.review_cycle_months} onChange={e => setCreateForm({ ...createForm, review_cycle_months: Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea rows={3} value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={submitCreate} disabled={saving} data-testid="create-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A criar…</> : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View policy dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setSelectedVersion(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="policy-view-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle>{viewing?.title}</DialogTitle>
                <DialogDescription>{viewing?.description}</DialogDescription>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="secondary" className={STATUS_META[viewing?.status]?.tone || 'bg-slate-100'}>{STATUS_META[viewing?.status]?.label || viewing?.status}</Badge>
                  {viewing?.current_version && <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">v{viewing.current_version} aprovada</Badge>}
                  {(viewing?.legal_refs || []).map((r, i) => <Badge key={i} variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">⚖️ {r}</Badge>)}
                </div>
              </div>
              {canEdit && (
                <Button variant="outline" onClick={draftAi} disabled={drafting} data-testid="draft-ai-btn">
                  {drafting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />A gerar…</> : <><Wand2 className="h-3.5 w-3.5 mr-1.5" />Gerar com IA</>}
                </Button>
              )}
            </div>
          </DialogHeader>

          {viewing && (viewing.versions || []).length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded">
              <Sparkles className="h-12 w-12 text-indigo-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Ainda não há conteúdo</h3>
              <p className="text-sm text-slate-600 mb-4">Clique em <strong>&quot;Gerar com IA&quot;</strong> para criar automaticamente a primeira versão a partir do template e do contexto da sua empresa (Claude Sonnet 4.6).</p>
              {viewing.outline && (
                <div className="text-left inline-block bg-white rounded p-4 border border-slate-200">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Estrutura do template</p>
                  <ul className="text-sm text-slate-700 space-y-1">
                    {viewing.outline.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {viewing && (viewing.versions || []).length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {viewing.versions.map(v => (
                  <Button
                    key={v.version}
                    variant={selectedVersion?.version === v.version ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedVersion(v)}
                    data-testid={`version-btn-${v.version}`}
                  >
                    v{v.version} {v.status === 'approved' && <ShieldCheck className="h-3 w-3 ml-1 text-emerald-600" />}
                    {v.ai_generated && <Sparkles className="h-3 w-3 ml-1 text-indigo-500" />}
                  </Button>
                ))}
              </div>

              {selectedVersion && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-slate-500">
                      Criada em {(selectedVersion.created_at || '').slice(0, 10)} por {selectedVersion.created_by}
                      {selectedVersion.ai_generated && <span className="ml-2 text-indigo-600">✨ IA</span>}
                      {selectedVersion.approved_at && (
                        <span className="ml-2 text-emerald-700">✓ Aprovada em {selectedVersion.approved_at.slice(0, 10)} por {selectedVersion.approved_by}</span>
                      )}
                    </div>
                    {canEdit && selectedVersion.status !== 'approved' && (
                      <Button size="sm" onClick={() => approveVersion(selectedVersion)} disabled={approving === selectedVersion.version} data-testid="approve-version-btn">
                        {approving === selectedVersion.version ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                        Aprovar com assinatura
                      </Button>
                    )}
                  </div>
                  {selectedVersion.signature_hash && (
                    <div className="text-[10px] font-mono text-slate-400 mb-2">SHA-256: {selectedVersion.signature_hash}</div>
                  )}
                  <div className="bg-slate-50 rounded p-4 border border-slate-200 max-h-[45vh] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">{selectedVersion.content_md}</pre>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Historical review snapshots — audit-grade evidence of "what was reviewed and when" */}
          {snapshots.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200" data-testid="policy-snapshots-section">
              <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-indigo-600" /> Histórico de Revisões ({snapshots.length})
              </h4>
              <div className="space-y-2">
                {snapshots.map((s, idx) => (
                  <div key={s.snapshot_id} className="border border-slate-200 rounded-md p-3 bg-slate-50 text-xs" data-testid={`snapshot-${s.snapshot_id}`}>
                    <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                      <span className="font-mono text-slate-500">{s.snapshot_id}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-600">
                          {(s.reviewed_at || '').slice(0, 10)} · Ciclo {s.next_cycle_months}m
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => openDiff(s.snapshot_id, idx === 0 ? 'current' : 'previous')}
                          data-testid={`snapshot-diff-${s.snapshot_id}`}
                        >
                          <GitCompare className="h-3 w-3 mr-1" />
                          {idx === 0 ? 'vs atual' : 'vs anterior'}
                        </Button>
                      </div>
                    </div>
                    <div className="text-slate-700 space-y-0.5">
                      <div><span className="text-slate-500">Revisto por:</span> <strong>{s.reviewed_by || '—'}</strong></div>
                      <div><span className="text-slate-500">Aprovado por:</span> <strong>{s.approved_by || '—'}</strong></div>
                      {s.review_task_id && (
                        <div><span className="text-slate-500">Tarefa:</span> <code className="bg-white px-1 rounded">{s.review_task_id}</code></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">
                Cada snapshot contém uma cópia integral da política no momento da revisão — evidência forte para auditoria CNCS.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setViewing(null); setSelectedVersion(null); }}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshot Diff Modal */}
      <Dialog open={!!diffData} onOpenChange={(o) => { if (!o) setDiffData(null); }}>
        <DialogContent className="max-w-3xl" data-testid="snapshot-diff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-indigo-600" /> Comparação de Snapshot
            </DialogTitle>
            <DialogDescription>
              {diffData && (
                <>
                  A comparar <strong>{diffData.before_label}</strong> com <strong>{diffData.after_label}</strong> — {diffData.changed_fields} campo(s) alterado(s).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {diffLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
          ) : diffData?.fields?.length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
              ✓ Sem alterações materiais entre as duas versões.
            </div>
          ) : diffData ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {diffData.fields.map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`diff-field-${f.field}`}>
                  <div className="bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 border-b border-slate-200">
                    {f.field}
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-200 text-xs">
                    <div className="bg-red-50 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-1">Antes</div>
                      <div className="text-red-900 whitespace-pre-wrap break-words">{f.before || <em className="text-slate-400">(vazio)</em>}</div>
                    </div>
                    <div className="bg-emerald-50 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Depois</div>
                      <div className="text-emerald-900 whitespace-pre-wrap break-words">{f.after || <em className="text-slate-400">(vazio)</em>}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiffData(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
